"use client";
import { Box, Flex, Heading, Text, useMediaQuery } from "@chakra-ui/react";
import { SectionTemplate } from "components/individuals/blocks";
import { ImageFragment } from "lib/gcms/gcms-typings";
import Image from "next/legacy/image";
import { Button, Button_T } from "@ui/components/button";
import { colors } from "@areahub/theme/src/foundations";
import { useAnalytics } from "@areahub/adapters/src/analytics";

export interface RequestDemoSectionProps {
  title: string;
  subtitle: string;
  additionalText: string;
  titleColor: string;
  subtitleColor: string;
  textColor: string;
  image: ImageFragment & { alt?: string };
  bg: string;
  buttons: Button_T[];
}

export const RequestDemoSection = (props: RequestDemoSectionProps) => {
  const [isLargeBp] = useMediaQuery("(min-width: 992px)");
  const analytics = useAnalytics();
  return (
    <SectionTemplate bg={props.bg}>
      <Flex
        align={"center"}
        flexDir={{
          lg: "row",
        }}
        gap={10}
      >
        {isLargeBp ? (
          <Box
            w={{
              lg: "55%",
              xl: "58%",
            }}
          >
            <Image
              {...props.image}
              src={props.image.url}
              placeholder={
                typeof props.image?.blurDataURL === "string"
                  ? "blur"
                  : undefined
              }
              blurDataURL={
                typeof props.image?.blurDataURL === "string"
                  ? props.image?.blurDataURL
                  : undefined
              }
            />
          </Box>
        ) : (
          <></>
        )}
        <Box w={{ lg: "45%", xl: "42%" }}>
          <Heading
            as={"h2"}
            color={props.titleColor}
            fontSize={{
              base: 26,
              md: 34,
              lg: 38,
              xl: 45,
            }}
            fontWeight={"bold"}
            lineHeight={1.2}
            mb={{
              base: 4,
              lg: 6,
            }}
          >
            {props.title}
          </Heading>
          <Text
            as={"h3"}
            color={props.subtitleColor}
            fontSize={{
              base: 16,
              md: 18,
              lg: 20,
              xl: 22,
            }}
            fontStyle={"italic"}
            mb={{
              base: 4,
              lg: 6,
            }}
          >
            {props.subtitle}
          </Text>
          {!isLargeBp ? (
            <Box pb={5} w={"70%"} mx={"auto"}>
              <Image
                {...props.image}
                src={props.image.url}
                alt="Map displaying AreaHub Enterprise Climate Analytics & Environmental Risk Intelligence for a specific address."
                placeholder={
                  typeof props.image?.blurDataURL === "string"
                    ? "blur"
                    : undefined
                }
                blurDataURL={
                  typeof props.image?.blurDataURL === "string"
                    ? props.image?.blurDataURL
                    : undefined
                }
              />
            </Box>
          ) : (
            <></>
          )}
          <Text
            color={props.textColor}
            fontSize={{
              base: 14,
              md: 16,
              lg: 17,
              xl: 18,
            }}
            mb={{
              base: 6,
              lg: 12,
            }}
            w={{
              lg: "90%",
            }}
          >
            {props.additionalText}
          </Text>
          <Flex
            justify={{
              base: "center",
              lg: "start",
            }}
          >
            <Button
              fontSize={{
                base: 12,
                md: 14,
                lg: 16,
                xl: 18,
              }}
              fontWeight={"bold"}
              py={{
                base: 4,
                lg: 7,
                xl: 8,
              }}
              w={{
                base: "140px",
                md: "160px",
                lg: "180px",
                xl: "220px",
              }}
              bgColor={colors.enterpriseColors.blue["200"]}
              color={"white"}
              textTransform={"uppercase"}
              onClick={() =>
                analytics.track(
                  "User clicked 'Request a Demo' button on enterprise landing page",
                  {
                    ui: "RequestDemoSection",
                  },
                )
              }
              {...props.buttons[0]}
            />
          </Flex>
        </Box>
      </Flex>
    </SectionTemplate>
  );
};
